import React, { useEffect, useRef, useState } from 'react';
import cc from 'classcat';
import { observer } from 'mobx-react';
import { useToggle, useWindowScroll } from 'react-use';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useRouter } from 'next/router';

import { Button, PhoneLink, SearchInput } from '../../index';
import {useSiteQuery, useStore} from '../../../hooks';
import isJobPage from '../../../lib/isJobPage';

import CloseIcon from '../../../images/icons/close.svg';
import PhoneIcon from '../../../images/icons/phone.svg';

import DesktopSkinnyMenu from './DesktopSkinnyMenu';
import DesktopSubMenu from './DesktopSubMenu';
import MainMenu from './MainMenu';
import SearchButton from './SearchButton';
import TopNavigation from './TopNavigation';

import styles from './styles.module.scss';
import query from "@/components/layout/Header/TopNavigation/topMenu.graphql";
import {renderCategoryLink} from "../../../lib/ContentRenderer";

interface Props {
  isHubPage: boolean;
}

const Header = ({ isHubPage }: Props) => {
  const { siteName, invertedLogo, careersLink } = useStore('globals');
  const router = useRouter();
  const globals = useStore('globals');
  const [mobileMenuOpen, toggleMobileMenu] = useToggle(false);
  const [searchActive, setSearchActive] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const { y } = useWindowScroll();
  const phone = globals.phone || null;

  const jobPageActive = router?.asPath === '/jobs' || isJobPage;
  const [{ data }] = useSiteQuery<TopMenuQuery>({ query });
  const menus = data?.topNavigation || null;

  const offset = 150;

  useEffect(() => {
    // istanbul ignore else
    if (mobileMenuRef.current) {
      if (mobileMenuOpen) {
        disableBodyScroll(mobileMenuRef.current);
      } else {
        enableBodyScroll(mobileMenuRef.current);
      }
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    const scrollY = window.scrollY > offset;
    setScrolled(scrollY);
  }, [y]);

  return (
    <header
      className={cc({
        [styles.header]: true,
        [styles.mobileMenuOpen]: mobileMenuOpen,
      })}
    >
      <div
        className={cc({
          [styles.searchOverlay]: true,
          [styles.searchActive]: searchActive,
        })}
      >
        <div className={styles.searchContainer}>
          <SearchInput active={searchActive} />
          <button className={styles.closeButton} onClick={() => setSearchActive(false)}>
            <CloseIcon width="24" height="24" />
          </button>
        </div>
      </div>

      { !jobPageActive &&
          <div className={cc({[styles.skinny]: true, [styles.scrolled]: scrolled})}>
            <div className={'container-skinny'}>
              <div className={'container-skinny-top'}>
                <a title="Click to return home" href={jobPageActive ? careersLink : '/'}
                    className={styles.invertedLogo}>
                  <img src={invertedLogo} alt={siteName}/>
                </a>
                <DesktopSkinnyMenu isJobPage={jobPageActive} searchClick={() => setSearchActive(true)}/>
              </div>

              <div className={'container-skinny-bottom'}>
                <MainMenu menuPosition="skinnyMenu"/>
              </div>
            </div>
          </div>
      }

      <div ref={mobileMenuRef} className={styles.mobileOverlay}>
        <div className={styles.menu}>
          <MainMenu menuPosition="header" />
          <TopNavigation />
          <div>
            <PhoneLink type="buttonWhite" className={styles.callUs}>
              <img src={'/_misc/phone-w.svg'} alt={''} />
              Call us
            </PhoneLink>
            <Button href="/contact-us" type="whiteBorder" className={styles.contactUs}>
              Contact us
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.navContainer}>
        <a href={jobPageActive ? careersLink : '/'} className={styles.mobileLogo}>
          <img src={'/_misc/logo.svg'} alt={siteName} />
        </a>

        <div className={cc({ container: true, [styles.headerContainer]: true })}>

          <div className={styles.desktopNavTop}>
            <a href={jobPageActive ? careersLink : '/'} className={styles.desktopLogo}>
              <img src={'/_misc/logo-amana-horizontal-no_byline.svg'} alt={siteName} />
            </a>
            <div className={styles.desktopLinkContainer}>
              <span>A part of the Anglican Community</span>
              <div className={styles.linkContainer}>
                {menus && (
                    <>{menus.map((m) => m?.linkField?.[0] && renderCategoryLink(m as TopNavigationCategory))}</>
                )}
              </div>
              {phone && (
                <a href={`tel:${phone.replace(/[^\d\+]/g, '')}`} title={'Click to give us a call'}>
                  Contact us: {phone}
                </a>
              )}
            </div>

          </div>

          <div className={styles.mobileMenu}>
            {isHubPage && (
                <PhoneLink type="button" className={styles.phoneLink}>
                  <PhoneIcon />
                  Call us
                </PhoneLink>
            )}

            {!jobPageActive && <SearchButton onClick={() => setSearchActive(true)} />}
            <button
                className={cc(['hamburger', 'hamburger--3dy', { 'is-active': mobileMenuOpen }])}
                onClick={toggleMobileMenu}
                type="button"
                aria-label="Open menu"
            >
              <span className="hamburger-box">
                <span className="hamburger-inner"></span>
              </span>
            </button>
          </div>
        </div>

        <DesktopSubMenu isJobPage={jobPageActive} searchClick={() => setSearchActive(true)} />
      </div>
    </header>
  );
};

export default observer(Header);
